import { useRouter, useSearchParams } from 'next/navigation';
import {
  Button,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  Label,
  UncontrolledDropdown
} from 'reactstrap';
import { Layout } from '../components/Layout';
import Meta from '../components/Meta';
import { Heading } from '../components/Heading';
import { Bounded } from '../components/Bounded';
import Paginate from '../components/Paginate';
import { LatestOrRelatedItem, PublicationItem } from '../slices/List';
import COUNTRIES from '../lib/countries';

// prismic document type to page title mapping
const PAGE_TITLES = {
  news: { name: 'News', title: 'All news' },
  project: { name: 'Projects', title: 'All projects' },
  publication: { name: 'Publications', title: 'All publications' }
};

const SearchPage = ({ results, navigation, settings }) => {
  const searchParams = useSearchParams();
  const type = searchParams.get('type') || 'news';
  const tags = searchParams.get('tags')?.split(',') || [];

  return (
    <Layout
      // alternateLanguages={page.alternate_languages} // TODO show all available languages
      navigation={navigation}
      settings={settings}
      page={{}}>
      <Meta
        meta={{
          title: PAGE_TITLES[type]?.title
        }}
        settings={settings}
      />
      <Bounded as="section">
        <Heading as="h1" className="mb-3">
          {PAGE_TITLES[type]?.title}
        </Heading>
        <div className="py-4">
          <Form>
            {Object.entries(PAGE_TITLES).map(([key, { name }]) => (
              <RadioItem label={name} type={key} key={key} />
            ))}
            {['project', 'publication'].includes(type) && <CountrySelect />}
            {tags.map((tag, i) => (
              <Button
                key={tag}
                outline
                size="sm"
                className="ms-2"
                href={searchRoute({
                  type,
                  // eslint-disable-next-line prettier/prettier
                  tags: tags.reduce((r, t, j) => (j === i ? r : r.concat([t])), [])
                })}>
                {tag} ⨯
              </Button>
            ))}
          </Form>
        </div>
        <div className="row">
          {type === 'publication'
            ? results.results.map((item) => (
                <PublicationItem item={item} key={item.id} />
              ))
            : results.results.map((item) => (
                <LatestOrRelatedItem item={item} key={item.id} />
              ))}
        </div>
        <div className="justify-content-center d-flex">
          <Paginate
            current={results.page}
            total={results.total_results_size}
            perPage={results.results_per_page}
          />
        </div>
      </Bounded>
    </Layout>
  );
};

export default SearchPage;

function RadioItem({ type, label }) {
  const params = useSearchParams();
  const pageType = params.get('type') || 'news';
  const router = useRouter();
  return (
    <FormGroup check inline>
      <Input
        id={type}
        onChange={() => router.push(searchRoute({ type }))}
        name="type"
        value={pageType}
        checked={type === pageType}
        type="radio"
      />{' '}
      <Label for={type} check>
        {label}
      </Label>
    </FormGroup>
  );
}

function CountrySelect() {
  const params = useSearchParams();
  const type = params.get('type') || 'news';
  const country = params.get('country');
  const selected_country = COUNTRIES[country];
  const router = useRouter();
  return (
    <UncontrolledDropdown
      active={!!country}
      size="sm"
      className="d-inline-block ms-2">
      <DropdownToggle caret color="outline-secondary">
        {selected_country && selected_country.flag}{' '}
        {selected_country ? selected_country.name : 'Country'}{' '}
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem onClick={() => router.push(searchRoute({ type }))}>
          All countries
        </DropdownItem>
        {Object.entries(COUNTRIES).map(([code, c]) => (
          <DropdownItem
            key={code}
            onClick={() => router.push(searchRoute({ type, country: code }))}>
            {c.flag} {c.name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

function searchRoute({ type, tags, country }) {
  const searchParams = new URLSearchParams();
  searchParams.set('type', type);
  if (['project', 'publication'].includes(type) && country)
    searchParams.set('country', country);
  if (tags && tags.length > 0) searchParams.set('tags', tags.join(','));
  return `/search?${searchParams.toString()}`;
}
